import { Injectable, Injector } from '@angular/core';
import { distinctUntilChanged, firstValueFrom, Observable, Subscription } from 'rxjs';
import { WFTableResource, WFMasterResource, WFStepConfig, WFRoleConfig, WFActionConfig, WFTaskConfig, ReorderResource, WFTableConfig, WFComponent, WFSectionConfig } from 'src/app/components/workflow/workflow';
import { CommentResource } from '../../controls/comments-and-attachments/services/comment';
import { Action, Fields, FormStatusEnum, FormType } from 'src/app/common/enumerations/enumerations';
import { WfTaskResource, WfTaskLocalResource, WFSectionLocalResource, WFConfiguration, WFTaskSignatureResource, SaveTaskResource, WfSignatureResource, WfSignature } from './workflow';
import { LogToShowResource } from '../../services/logs/log-to-show-resource';
import { SelectedElement } from 'src/app/components/checklists/checklist-builder/checklist-builder-map/checklist-builder-map.component';
import { CdkDragMove } from '@angular/cdk/drag-drop';
import { utils } from 'src/app/modules/libs/utils';
import { BaseService } from 'src/app/common/base/base.service';
import { Role } from '../catalogs/roles/services/role';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';
import { OpApprovalValue } from 'src/app/controls/checklist-components/op-approval-w-checklists/op-approval-w-checklists.component';
import { DocumentType } from 'src/app/services/documents/documents';


@Injectable()
export class WorkFlowService extends BaseService<WFTableConfig, number> {
  WorkFlowTask = '/WorkFlowTask';
  WFConfiguration = '/WFConfiguration';
  WorkFlow = '/WorkFlow';
  TransactionLogs = '/TransactionLogs';

  selectedNode?: SelectedElement;

  components!: WFComponent[];
  componentsSubs!: Subscription;

  roles?: Role[];
  rolesSubs!: Subscription;

  taskHover?: boolean;
  stepHover?: boolean;
  sectionHover?: boolean;

  loading?: boolean;

  constructor(
    protected override injector: Injector,
  ) {
    super(injector, '/workflow');
    this.loadComponents();
  }

  loadComponents() {
    this.componentsSubs = this.store.select((state) => state.Components.data).pipe(distinctUntilChanged()).subscribe((data) => {
      if (data.length) this.components = data;
    });
  }

  loadRoles() {
    this.rolesSubs = this.store.select(state => state.Roles.data).subscribe(data => {
      if (data?.length) {
        this.roles = data;
      }
    });
  }

  public readPending(id: number) {
    const httpresult = this.http.get<WfTaskResource[]>(this.BASE_URL + this.WorkFlowTask + '/Pending/' + id);
    return httpresult;
  }

  public readByWorkFlow(id: number) {
    const httpresult = this.http.get<WfTaskLocalResource[]>(this.BASE_URL + this.WorkFlowTask + '/ByWorkFlow/' + id);
    return httpresult;
  }

  public readBySection(id: number, sectionNumber: number, showInactive: boolean = false, amendmentId: number = 0) {
    const httpresult = this.http.get<any[]>(this.BASE_URL + this.WorkFlowTask + '/BySection/' + id + '?SectionNumber=' + sectionNumber + '&showInactive=' + (showInactive ? 'true' : 'false') + '&amendmentId=' + amendmentId);
    return httpresult;
  }

  public readAllBySection(id: number, sectionNumber: number) {
    const httpResult = this.http.get<any[]>(this.BASE_URL + this.WorkFlowTask + '/AllBySection/' + id + '?SectionNumber=' + sectionNumber);
    return httpResult;
  }

  public readAllSections(wfTableId: number): Observable<WFSectionLocalResource[]> {
    const httpresult = this.http.get<WFSectionLocalResource[]>(this.BASE_URL + this.WorkFlow + '/WFSectionLocals/' + wfTableId);
    return httpresult;
  }

  public readByWorkFlowBySection(id: number, sectionId: number) {
    const httpresult = this.http.get<WfTaskLocalResource[]>(this.BASE_URL + this.WorkFlowTask + '/ByWorkFlow/' + id + '?wfSectionConfig=' + sectionId);
    return httpresult;
  }

  public readConfiguration(type: number, wfTableId: number) {
    const httpresult = this.http.get<WFConfiguration[]>(this.BASE_URL + this.WFConfiguration + '/' + type + '/' + wfTableId);
    return httpresult;
  }

  public sign(taskSignature: WFTaskSignatureResource) {
    const url: string = this.BASE_URL + this.WorkFlowTask + '/Sign';
    const httpresult = this.http.put<any>(url, taskSignature);
    return httpresult;
  }

  public save(s: SaveTaskResource) {
    const httpresult = this.http.put<any>(this.BASE_URL + this.WorkFlowTask + '/Save/' + s.id, s);
    return httpresult;
  }

  public saveOpApproval(s: SaveTaskResource) {
    const httpresult = this.http.put<any>(this.BASE_URL + this.WorkFlowTask + '/SaveOpApproval/' + s.id, s);
    return httpresult;
  }

  public approveOpApproval(id: number, approve: boolean) {
    const httpresult = this.http.put<WfTaskLocalResource>(this.BASE_URL + this.WorkFlowTask + '/ApproveOpApproval/' + id + '/' + approve, null);
    return httpresult;
  }

  public copyTask(wfTaskLocalId: number, duplicatedTag: string | null = null): Observable<WfTaskLocalResource> {
    const httpresult = this.http.post<WfTaskLocalResource>(this.BASE_URL + this.WorkFlowTask + '/CopyTask/' + wfTaskLocalId + '/' + duplicatedTag, null);
    return httpresult;
  }

  public removeTask(wfTaskLocalId: number) {
    const httpresult = this.http.post<any>(this.BASE_URL + this.WorkFlowTask + '/RemoveTask/' + wfTaskLocalId, null);
    return httpresult;
  }

  public readAmendmentTasks(wfTableID: number): Observable<any[]> {
    return this.http.get<any[]>(this.BASE_URL + this.WorkFlowTask + '/Amendments/' + wfTableID.toString());
  }

  public readCurrentAmendmentID(wfTableID: number): Observable<number> {
    return this.http.get<number>(this.BASE_URL + this.WorkFlow + '/CurrentAmendmentID/' + wfTableID.toString());
  }

  public getLogsBySn(SN: string): Observable<LogToShowResource> {
    return this.http.get<LogToShowResource>(this.BASE_URL + this.WorkFlow + '/GetLogsBySn/' + SN);
  }

  ////#region WF Configurator

  public getWFTable(): Observable<WFTableResource[]> {
    const httpresult = this.http.get<WFTableResource[]>(this.BASE_URL + this.WorkFlow);
    return httpresult;
  }

  public getWFTableByForm(formType: number): Observable<WFTableConfig[]> {
    const httpresult = this.http.get<WFTableConfig[]>(this.BASE_URL + this.WorkFlow + '/ByFormType/' + formType);
    return httpresult;
  }

  public getWFSectionsByForm(formType: number): Observable<WFSectionConfig[]> {
    const httpresult = this.http.get<WFSectionConfig[]>(this.BASE_URL + this.WorkFlow + '/SectionsByFormType/' + formType);
    return httpresult;
  }

  public getWFMaster(id: number): Observable<WFMasterResource[]> {
    const httpresult = this.http.get<WFMasterResource[]>(this.BASE_URL + this.WorkFlow + '/MasterConfig/' + id);
    return httpresult;
  }

  public getWFSteps(id: number): Observable<WFStepConfig[]> {
    const httpresult = this.http.get<WFStepConfig[]>(this.BASE_URL + this.WorkFlow + '/StepConfig/' + id);
    return httpresult;
  }

  public getWFStepConfigs(id: number): Observable<WFStepConfig[]> {
    const httpresult = this.http.get<WFStepConfig[]>(this.BASE_URL + this.WorkFlow + '/AllStepConfigs/' + id);
    return httpresult;
  }

  public getDocumentTypeByWFTableID(wfTableID: number): Observable<DocumentType> {
    const httpresult = this.http.get<DocumentType>(this.BASE_URL + this.WFConfiguration + '/' + wfTableID);
    return httpresult;
  }

  public approveWFTableConfig(wfTableConfigID: number): Observable<WFTableConfig> {
    const httpresult = this.http.put<WFTableConfig>(this.BASE_URL + this.WorkFlow + '/ApproveWFTableConfig/' + wfTableConfigID, null);
    return httpresult;
  }

  /// WFRoleConfig

  public postWFRole(wfRole: WFRoleConfig): Observable<WFRoleConfig> {
    const httpresult = this.http.post<WFRoleConfig>(this.BASE_URL + this.WorkFlow + '/RoleConfig', wfRole);
    return httpresult;
  }

  public putWFRole(wfRole: WFRoleConfig): Observable<WFRoleConfig> {
    const httpresult = this.http.put<WFRoleConfig>(this.BASE_URL + this.WorkFlow + '/RoleConfig/' + wfRole.id, wfRole);
    return httpresult;
  }

  public delWFRole(wfRole: WFRoleConfig) {
    const httpresult = this.http.delete(this.BASE_URL + this.WorkFlow + '/RoleConfig/' + wfRole.id);
    return httpresult;
  }

  /// WFActionConfig

  public postWFAction(wfAction: WFActionConfig): Observable<WFActionConfig> {
    const httpresult = this.http.post<WFActionConfig>(this.BASE_URL + this.WorkFlow + '/ActionConfig', wfAction);
    return httpresult;
  }

  public putWFAction(wfAction: WFActionConfig): Observable<WFActionConfig> {
    const httpresult = this.http.put<WFActionConfig>(this.BASE_URL + this.WorkFlow + '/ActionConfig/' + wfAction.id, wfAction);
    return httpresult;
  }

  public delWFAction(wfAction: WFActionConfig) {
    const httpresult = this.http.delete(this.BASE_URL + this.WorkFlow + '/ActionConfig/' + wfAction.id);
    return httpresult;
  }

  /// WFTaskConfig

  public postWFTask(wfTask: WFTaskConfig): Observable<WFTaskConfig> {
    const httpresult = this.http.post<WFTaskConfig>(this.BASE_URL + this.WorkFlow + '/TaskConfig', wfTask);
    return httpresult;
  }

  public putWFTask(wfTask: WFTaskConfig): Observable<WFTaskConfig> {
    const httpresult = this.http.put<WFTaskConfig>(this.BASE_URL + this.WorkFlow + '/TaskConfig/' + wfTask.id, wfTask);
    return httpresult;
  }

  public putWFTasks(wfTasks: WFTaskConfig[]): Observable<WFTaskConfig> {
    const httpresult = this.http.put<WFTaskConfig>(this.BASE_URL + this.WorkFlow + '/TaskConfigs/', wfTasks);
    return httpresult;
  }

  public delWFTask(wfTask: WFTaskConfig) {
    const httpresult = this.http.delete(this.BASE_URL + this.WorkFlow + '/TaskConfig/' + wfTask.id);
    return httpresult;
  }

  /// WFStepConfig

  public postWFStep(wfStep: WFStepConfig): Observable<WFStepConfig> {
    const httpresult = this.http.post<WFStepConfig>(this.BASE_URL + this.WorkFlow + '/StepConfig', wfStep);
    return httpresult;
  }

  public putWFStep(wfStep: WFStepConfig): Observable<WFStepConfig> {
    const httpresult = this.http.put<WFStepConfig>(this.BASE_URL + this.WorkFlow + '/StepConfig/' + wfStep.id, wfStep);
    return httpresult;
  }

  public delWFStep(wfStep: WFStepConfig) {
    const httpresult = this.http.delete(this.BASE_URL + this.WorkFlow + '/StepConfig/' + wfStep.id);
    return httpresult;
  }

  /// WFSectionConfig

  public postWFSection(wfSection: WFMasterResource): Observable<WFMasterResource> {
    const httpresult = this.http.post<WFMasterResource>(this.BASE_URL + this.WorkFlow + '/SectionConfig', wfSection);
    return httpresult;
  }

  public putWFSection(wfSection: WFMasterResource): Observable<WFMasterResource> {
    const httpresult = this.http.put<WFMasterResource>(this.BASE_URL + this.WorkFlow + '/SectionConfig/' + wfSection.id, wfSection);
    return httpresult;
  }

  public delWFSection(wfSection: WFMasterResource) {
    const httpresult = this.http.delete(this.BASE_URL + this.WorkFlow + '/SectionConfig/' + wfSection.id);
    return httpresult;
  }

  /// WFTableConfig

  public getWFTableConfigByID(id: number): Observable<WFTableConfig> {
    const httpresult = this.http.get<WFTableConfig>(this.BASE_URL + this.WorkFlow + '/WFTableConfig/' + id);
    return httpresult;
  }

  public duplicateWFTableConfig(id: number): Observable<WFTableConfig> {
    const httpresult = this.http.get<WFTableConfig>(this.BASE_URL + this.WorkFlow + '/DuplicateWFTableConfig/' + id);
    return httpresult;
  }

  public postWFTableConfig(wfTable: WFTableConfig): Observable<WFTableConfig> {
    const httpresult = this.http.post<WFTableConfig>(this.BASE_URL + this.WorkFlow + '/WFTableConfig', wfTable);
    return httpresult;
  }

  public putWFTableConfig(wfTable: WFTableConfig): Observable<WFTableConfig> {
    const httpresult = this.http.put<WFTableConfig>(this.BASE_URL + this.WorkFlow + '/WFTableConfig/' + wfTable.id, wfTable);
    return httpresult;
  }

  public deleteWFTableConfig(wfTable: WFTableConfig) {
    const httpresult = this.http.delete(this.BASE_URL + this.WorkFlow + '/WFTableConfig/' + wfTable.id);
    return httpresult;
  }


  //////#endregion
  public reorderItems(reorder: ReorderResource[], level: number) {
    const httpresult = this.http.patch(this.BASE_URL + this.WorkFlow + '/ReorderItems/' + level, reorder);
    return httpresult;
  }
  public cancelTask(wfTableID: number): Observable<WfSignatureResource> {
    return this.http.get<WfSignatureResource>(this.BASE_URL + this.WorkFlowTask + '/Cancel/' + wfTableID.toString());
  }
  public amendmendTask(wfTableID: number, type: FormType): Observable<WfSignatureResource> {
    return this.http.get<WfSignatureResource>(this.BASE_URL + this.WorkFlowTask + '/Amendment/' + type.toString() + '/' + wfTableID.toString());
  }
  public comments(wfTableID: number): Observable<CommentResource[]> {
    return this.http.get<CommentResource[]>(this.BASE_URL + this.WorkFlowTask + '/Comments/' + wfTableID.toString());
  }

  pointerY!: number;

  onDragMoved(e: CdkDragMove) {
    const headerH = 170;
    const scrollThreshold = 50;
    const scrollDistance = 50;
    const scrollableContainer = document.querySelector('.sidenav-content');
    const clientHeight = scrollableContainer?.clientHeight ?? 0;
    // console.log('pointer: ' + this.pointerY + ' - height: ' + (clientHeight - 50));
    if (this.pointerY > clientHeight - scrollThreshold + headerH)
      scrollableContainer?.scrollBy({ top: scrollDistance, left: 0 });
    if (this.pointerY < headerH + scrollThreshold)
      scrollableContainer?.scrollBy({ top: -scrollDistance, left: 0 });
  }

  getSignature(t: WFTaskConfig) {
    this.loadComponents();
    const signature: WfSignature = {
      id: t.id,
      type: t.type,
      component: this.components.find((c) => c.id == t.type),
      number: t.component?.numericHeader
        ? (t.name ?? 0 + 1).toString()
        : null,
      name: t.name,
      code: t.code,
      question: t.name3,
      question2: t.name2,
      question3: t.name4,
      options: utils.JSONparse(t.options)
        ? utils.JSONparse(t.options)
        : [],
      condition: t.condition,
      configuration: t.configuration,
      color: utils.JSONparse(t.configuration)?.color,
      order: t.order,
      disabled: true,
      enabled: true,
      stepId: t.wfStepConfigID,
      requesterSignature: t.requesterSignature,
      roles: t.wfRoleConfigs?.map((x) => x.roleID),
      roleCodes: t.wfRoleConfigs?.map((x) => x.role?.code).join('/'),
      approveAction:
        t.wfActionConfigs?.find((x) => x.action == Action.Approve) != null,
      approveActionId: t.wfActionConfigs?.find((x) => x.action == Action.Approve)
        ?.id,
      unapproveAction:
        t.wfActionConfigs?.find((x) => x.action == Action.Unapprove) != null,
      unapproveActionId: t.wfActionConfigs?.find((x) => x.action == Action.Unapprove)
        ?.id,
      disapproveAction:
        t.wfActionConfigs?.find((x) => x.action == Action.Disapprove) != null,
      disapproveActionId: t.wfActionConfigs?.find(
        (x) => x.action == Action.Disapprove
      )?.id,
      amendmentAction:
        t.wfActionConfigs?.find((x) => x.action == Action.Amendment) != null,
      amendmentActionId: t.wfActionConfigs?.find(
        (x) => x.action == Action.Amendment
      )?.id,
      cancelAction:
        t.wfActionConfigs?.find((x) => x.action == Action.Cancel) != null,
      cancelActionId: t.wfActionConfigs?.find(
        (x) => x.action == Action.Cancel
      )?.id,
      invalid: this.hasErrors(),

      approveNextStepConfigID: t.wfActionConfigs?.find(
        (x) => x.action == Action.Approve)?.wfNextStepConfigID,
      unapproveNextStepConfigID: t.wfActionConfigs?.find(
        (x) => x.action == Action.Unapprove)?.wfNextStepConfigID,
      disapproveNextStepConfigID: t.wfActionConfigs?.find(
        (x) => x.action == Action.Disapprove
      )?.wfNextStepConfigID,
      amendmentNextStepConfigID: t.wfActionConfigs?.find(
        (x) => x.action == Action.Amendment
      )?.wfNextStepConfigID,
      cancelNextStepConfigID: t.wfActionConfigs?.find(
        (x) => x.action == Action.Cancel
      )?.wfNextStepConfigID
    };

    return signature;
  }

  getTaskConfigFromSignature(signature: WfSignature): WFTaskConfig {
    this.loadRoles();
    return {
      id: signature.id,
      type: signature.type,
      component: signature.component,
      name: signature.name,
      name3: signature.question,
      name2: signature.question2,
      name4: signature.question3,
      code: signature.code,
      condition: signature.condition,
      configuration: signature.configuration,
      order: signature.order,
      wfStepConfigID: signature.stepId,
      wfRoleConfigs: signature.roles?.map(roleID => ({
        roleID,
        role: this.roles?.find(r => r.id === roleID),
      })) as WFRoleConfig[],
      wfActionConfigs: [
        ...(signature.approveAction
          ? [{ action: Action.Approve, id: signature.approveActionId, name: 'Approve', label: 'Approve', wfNextStepConfigID: signature.approveNextStepConfigID }]
          : []),
        ...(signature.unapproveAction
          ? [{ action: Action.Unapprove, id: signature.unapproveActionId, name: 'Unapprove', label: 'Unapprove', wfNextStepConfigID: signature.unapproveNextStepConfigID }]
          : []),
        ...(signature.disapproveAction
          ? [{ action: Action.Disapprove, id: signature.disapproveActionId, name: 'Disapprove', label: 'Disapprove', wfNextStepConfigID: signature.disapproveNextStepConfigID }]
          : []),
        ...(signature.amendmentAction
          ? [{ action: Action.Amendment, id: signature.amendmentActionId, name: 'Amendment', label: 'Amendment', wfNextStepConfigID: signature.amendmentNextStepConfigID }]
          : []),
        ...(signature.cancelAction
          ? [{ action: Action.Cancel, id: signature.cancelActionId, name: 'Cancel', label: 'Cancel', wfNextStepConfigID: signature.cancelNextStepConfigID }]
          : []),
      ] as WFActionConfig[],
      options: JSON.stringify(signature.options),
      enabled: signature.enabled,
      required: true,
      requesterSignature: signature.requesterSignature,
      status: 1
    };
  }

  hasErrors() {
    return false;
  }

  getFormProperties(formType: FormType) {
    const httpresult = this.http.get<string[]>(this.BASE_URL + this.WorkFlow + '/FormProperties/' + formType);
    return httpresult;
  }

  /**
 * Updates the operational approval task text, determining whether to save directly or mark as pending change based on form status and user privileges.
 * Answers saved directly (drafting, or with the no-approval privilege) are stored as approved so OIC can sign them.
 * @param formType - The form type associated with the task.
 * @param jsonValue - The updated text content.
 * @param wfTaskLocalID - Local identifier for the workflow task.
 * @param formStatus - Current status of the form.
 * @param noApprovalPriv - Privilege enum for bypassing approval.
 */
  async opApprovalTaskChanged(formType: FormType, jsonValue: string, wfTaskLocalID: number, formStatus: FormStatusEnum, noApprovalPriv: PrivilegeEnum) {

    const canChangeWoApproval = this.hasPrivilege(noApprovalPriv);
    const status = formStatus;
    const isNewLike =
      status === FormStatusEnum.Draft ||
      status === FormStatusEnum.AmendmentDrafting ||
      status === FormStatusEnum.New ||
      canChangeWoApproval; // No Pending Change, Just save

    const pendingChange = !!status && !isNewLike && !!jsonValue;
    const pendingChangeObj = utils.JSONparse(jsonValue) as OpApprovalValue;

    // Answers given while drafting are the initial answers and need no change approval,
    // so they are stored as approved (otherwise OIC Approve stays hidden for manual checks).
    pendingChangeObj.approved = isNewLike;
    pendingChangeObj.formStatus = status;

    jsonValue = utils.JSONstringify(pendingChangeObj);

    const saveTask: SaveTaskResource = {
      id: wfTaskLocalID ?? 0,
      text: jsonValue,
      formType,
      answerChangedById: (pendingChange || canChangeWoApproval) ? this.currentUser!.id : null,
      field: Fields.TEXT,
      pendingChange,
      pendingChangeObj: pendingChange ? jsonValue : null
    };
    await firstValueFrom(this.saveOpApproval(saveTask));
  }
}
